import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'
import { Trans } from '@lingui/react/macro'

import { useLocation, type Location } from 'react-router-dom'

import { YStack, XStack, Separator } from 'tamagui'
import {
  Paragraph,
  UserDefaultPhoto,
  UserPrimaryPhoto,
  Card,
  FormattedMarkdown,
  H2,
  Link,
  Button,
  Icon,
} from '@newComponents'

import { EventScreenPresentations_event$key } from './__generated__/EventScreenPresentations_event.graphql'
import { EVENT_PRESENTATION_MARKDOWN_ELEMENTS } from './eventPresentationMarkdown'

type BackgroundLocationState = {
  backgroundLocation?: Location
}

const isLocation = (value: unknown): value is Location =>
  typeof value === 'object' &&
  value !== null &&
  'pathname' in value &&
  typeof (value as { pathname?: unknown }).pathname === 'string'

const getBackgroundLocationState = (
  state: unknown
): BackgroundLocationState => {
  if (typeof state !== 'object' || state === null) {
    return {}
  }

  const record = state as { backgroundLocation?: unknown }

  return {
    backgroundLocation: isLocation(record.backgroundLocation)
      ? record.backgroundLocation
      : undefined,
  }
}

export const EventScreenPresentations = ({
  event,
}: {
  event: EventScreenPresentations_event$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenPresentations_event on Event {
        prettyUrl

        viewerCanManage

        presentations {
          edges {
            node {
              rowId
              prettyUrl

              title
              description

              presenterFirstName
              presenterLastName

              # Signed URL for guest presenter avatar (when no linked User)
              guestPresenterAvatarUrl(transform: { w: 48, h: 48, dpr: 3 })

              presenter {
                firstName
                lastName

                ...UserPrimaryPhotoNew_user
              }
            }

            cursor
          }
        }
      }
    `,
    event
  )

  const location = useLocation() as unknown as Location
  const locationState = useMemo(
    () => getBackgroundLocationState(location.state),
    [location.state]
  )

  const canCreate = useMemo(() => {
    return data.viewerCanManage
  }, [data])

  const createButton = canCreate ? (
    <Link
      href={`/events/${data.prettyUrl}/settings/presentations/new`}
      options={{ state: { backgroundLocation: location } }}
      hoverStyle={{ textDecorationLine: 'none' }}
    >
      <Button
        errorFlash={false}
        margin={-6}
        small
        secondary
        paddingHorizontal='$2'
        gap='$1'
      >
        <Icon name='add-filled' size='$3' />

        <Button.Text size='$4' secondary>
          <Trans>Create</Trans>
        </Button.Text>
      </Button>
    </Link>
  ) : null

  if (!data.presentations.edges.length) {
    if (canCreate) {
      return (
        <Card glass gap='$5'>
          <XStack>
            <Paragraph size='$4' color='$colorMuted' fontWeight='$5' flex={1}>
              <Trans>Presentations</Trans>
            </Paragraph>

            {createButton}
          </XStack>

          <YStack gap='$4' alignItems='center'>
            <Paragraph
              textAlign='center'
              size='$4'
              fontWeight='$6'
              color='$colorMuted'
              maxWidth={400}
            >
              <Trans>
                Presentations are the individual talks, workshops, or other
                content for this event.
              </Trans>
            </Paragraph>

            <YStack gap='$1'>
              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                <Trans>
                  You can Create a Presentation with the button above.
                </Trans>
              </Paragraph>

              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                <Trans>Give it a Title, Description, and the Presenter.</Trans>
              </Paragraph>

              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                <Trans>
                  Presenters can then Edit the information on their
                  Presentations.
                </Trans>
              </Paragraph>

              <Paragraph
                textAlign='center'
                size='$4'
                fontWeight='$6'
                color='$colorMuted'
                maxWidth={400}
              >
                <Trans>
                  After the event, you can add a link to a video recording or
                  their slides on to the same Presentation so everything&apos;s
                  in one place.
                </Trans>
              </Paragraph>
            </YStack>

            <Paragraph
              textAlign='center'
              size='$4'
              fontWeight='$6'
              color='$colorMuted'
              maxWidth={400}
            >
              <Trans>
                Presentations are displayed on Event Listings, Emails, your
                Guild&apos;s Profile, as well as the Presenter&apos;s profile.
              </Trans>
            </Paragraph>
          </YStack>
        </Card>
      )
    }

    return null
  }

  return (
    <Card glass gap='$5'>
      <XStack>
        <Paragraph size='$4' color='$colorMuted' fontWeight='$5' flex={1}>
          <Trans>Presentations</Trans>
        </Paragraph>

        {createButton}
      </XStack>

      <YStack gap='$6'>
        {data.presentations.edges.map((edge, index) => {
          return (
            <Link
              key={edge.cursor}
              href={`/presentations/${edge.node.prettyUrl}`}
              options={{
                state: {
                  backgroundLocation:
                    locationState.backgroundLocation || location,
                },
              }}
              underlineHover={false}
            >
              <YStack gap='$6'>
                {!!index && <Separator borderColor='$borderColor' />}

                <XStack gap='$5'>
                  <YStack gap='$2' alignItems='center' width={20 * 4}>
                    {edge.node.presenter ? (
                      <>
                        <UserPrimaryPhoto
                          user={edge.node.presenter}
                          size='$12'
                        />

                        <Paragraph size='$4' textAlign='center'>
                          {edge.node.presenter.firstName}{' '}
                          {edge.node.presenter.lastName}
                        </Paragraph>
                      </>
                    ) : (
                      <>
                        <UserDefaultPhoto
                          defaultAvatarUrl={edge.node.guestPresenterAvatarUrl}
                          width={12 * 4}
                          height={12 * 4}
                          borderRadius={12 * 4}
                        />

                        <Paragraph size='$4' textAlign='center'>
                          {edge.node.presenterFirstName}{' '}
                          {edge.node.presenterLastName}
                        </Paragraph>
                      </>
                    )}
                  </YStack>

                  <YStack gap='$2' flex={1}>
                    <H2 fontWeight='$6'>{edge.node.title}</H2>

                    <FormattedMarkdown
                      allowedElements={EVENT_PRESENTATION_MARKDOWN_ELEMENTS}
                      textSize='$4'
                      formattedVariant='PLAIN_TEXT'
                    >
                      {edge.node.description}
                    </FormattedMarkdown>
                  </YStack>
                </XStack>
              </YStack>
            </Link>
          )
        })}
      </YStack>
    </Card>
  )
}
